import { MutableRefObject, useEffect, useRef, useMemo, useState, VoidFunctionComponent, useCallback } from 'react';
import { useKey } from 'react-use';
import { Color, MathUtils, MeshBasicMaterial, Path, Shape, Vector2 } from 'three';
import { ThreeEvent, useFrame } from '@react-three/fiber';
import { Line, Text } from '@react-three/drei';
import { pathTo3D as drawBoxEdges } from '@/lib/pathTo3D';
import { hooge } from '@/lib/fonts';
import selectionFocusSound from '@/sounds/ob-sfx-move-selection-focus.mp3';
import enterSound from '@/sounds/ob-sfx-codebox-digit-input.mp3';
import deleteSound from '@/sounds/ob-sfx-codebox-digit-delete.mp3';
import { useOnlybotsSound } from '@/hooks/useOnlybotsSound';

const KEY_BG_RADIUS = 0.0765;
const ROUNDED_RADIUS = 0.42;
const KEY_WIDTH = 1.08;
const KEY_HEIGHT = 0.7;
const KEY_VERTICAL_PADDING = 0.12;
const KEY_HORIZONTAL_PADDING = 0.14;
const FOCUS_FADE_TIME = 0.125;

const drawBoxBg = (points: Vector2[]): Shape => {
    const shape = new Shape();
    shape.setFromPoints(points);
    return shape;
};

type BoxRadius = [number, number, number, number];

const getBoxPoints = (boxRadius: BoxRadius): Vector2[] => {
    const left = 0;
    const top = 0;
    const right = left + KEY_WIDTH;
    const bottom = top - KEY_HEIGHT;
    const path = new Path();
    // draw line with rounded corners within bounds
    path.moveTo(left, top - boxRadius[0]);
    path.quadraticCurveTo(left, top, left + boxRadius[0], top);
    path.lineTo(right - boxRadius[1], top);
    path.quadraticCurveTo(right, top, right, top - boxRadius[1]);
    path.lineTo(right, bottom + boxRadius[2]);
    path.quadraticCurveTo(right, bottom, right - boxRadius[2], bottom);
    path.lineTo(left + boxRadius[3], bottom);
    path.quadraticCurveTo(left, bottom, left, bottom + boxRadius[3]);
    path.lineTo(left, top - boxRadius[0]);
    return path.getPoints();
};

const getBoxRadius = (key: string): BoxRadius => {
    if (key === '1') {
        return [ROUNDED_RADIUS, KEY_BG_RADIUS, KEY_BG_RADIUS, KEY_BG_RADIUS];
    } else if (key === '3') {
        return [KEY_BG_RADIUS, ROUNDED_RADIUS, KEY_BG_RADIUS, KEY_BG_RADIUS];
    } else if (key === '@') {
        return [KEY_BG_RADIUS, KEY_BG_RADIUS, KEY_BG_RADIUS, ROUNDED_RADIUS];
    } else if (key === 'del') {
        return [0.48, KEY_BG_RADIUS, 0.45, 0.3];
    }
    return [KEY_BG_RADIUS, KEY_BG_RADIUS, KEY_BG_RADIUS, KEY_BG_RADIUS];
};

interface Props {
    value: string;
    index: number;
    onKeyPress: (code: string) => void;
}

export const Key: VoidFunctionComponent<Props> = ({ value, index, onKeyPress }) => {
    const [playSelectionFocusSound] = useOnlybotsSound(selectionFocusSound);
    const [playEnterSound] = useOnlybotsSound(enterSound);
    const [playDeleteSound] = useOnlybotsSound(deleteSound);
    const column = index % 3;
    const row = Math.floor(index / 3);
    const left = column * (KEY_WIDTH + KEY_HORIZONTAL_PADDING) - (KEY_WIDTH + KEY_HORIZONTAL_PADDING);
    const top = (3 * (KEY_HEIGHT + KEY_VERTICAL_PADDING)) / 2 - row * (KEY_HEIGHT + KEY_VERTICAL_PADDING);
    const boxRadius = getBoxRadius(value);
    const points = getBoxPoints(boxRadius);
    const line = drawBoxEdges(points);
    const shape = drawBoxBg(points);
    const materialRef = useRef() as MutableRefObject<MeshBasicMaterial>;
    const timer = useRef<number>(0);
    const keyFocused = useRef<boolean>(false);
    const [hovered, setHovered] = useState<boolean>(false);
    const over = (e: ThreeEvent<PointerEvent>): void => {
        // by checking if object type is mesh, we prevent sound from being played
        // when other elements are hovered over, eg. Line and Text
        // object is analagous to target, eventObject to currentTarget
        if (e.object.type === 'Mesh') {
            playSelectionFocusSound();
        }
        setHovered(true);
    };
    const out = (): void => {
        setHovered(false);
    };
    useEffect(() => {
        if (hovered) {
            document.body.style.cursor = 'pointer';
            keyFocused.current = true;
        }
        return () => {
            document.body.style.cursor = 'auto';
        };
    }, [hovered]);

    const onPress = useCallback(() => {
        onKeyPress(value.toLowerCase());
        timer.current = 0;
        keyFocused.current = true;
        if (value === 'del') {
            playDeleteSound();
        } else {
            playEnterSound();
        }
    }, [onKeyPress, playDeleteSound, playEnterSound, value]);

    useKey(value, onPress, undefined, [onPress]);

    // add alternate key mappings, we need to add these to every key component
    // since react doesn't like hooks behind conditionals
    useKey('.', value === '@' ? onPress : undefined, undefined, [onPress]);
    useKey('Backspace', value === 'del' ? onPress : undefined, undefined, [onPress]);

    const focusColor = useMemo(() => new Color(value === 'del' ? 0xff0000 : 0x97b5ec), [value]);
    const normalColor = useMemo(() => new Color(value === 'del' ? 0x5f2b28 : 0x5a637d), [value]);

    useFrame((_, delta) => {
        if (keyFocused.current) {
            if (timer.current === 0 || hovered) {
                timer.current = delta;
                materialRef.current.color.set(focusColor);
            } else if (timer.current > 0 && timer.current < FOCUS_FADE_TIME) {
                timer.current += delta;
                materialRef.current.color.lerpColors(
                    focusColor,
                    normalColor,
                    MathUtils.clamp(timer.current / FOCUS_FADE_TIME, 0, 1)
                );
            } else if (timer.current > FOCUS_FADE_TIME) {
                materialRef.current.color.set(normalColor);
                keyFocused.current = false;
            }
        }
    });

    return (
        <group position={[left, top, 0]} onPointerOver={over} onPointerOut={out}>
            <group position={[-KEY_WIDTH / 2, KEY_HEIGHT / 2, 0]}>
                {value === 'del' ? (
                    <mesh onClick={onPress}>
                        <shapeGeometry attach="geometry" args={[shape]} />
                        <meshBasicMaterial key={value} ref={materialRef} color={'#5f2b28'} />
                    </mesh>
                ) : (
                    <>
                        <Line position={[0, 0, 0.01]} points={line} color={0xe0d0d0} lineWidth={2} />
                        <mesh onClick={onPress}>
                            <shapeGeometry attach="geometry" args={[shape]} />
                            <meshBasicMaterial key={value} ref={materialRef} color={'#57617e'} />
                        </mesh>
                    </>
                )}
            </group>
            <Text
                strokeWidth={0.002}
                strokeColor={0xe0d0d0}
                font={hooge}
                fontSize={0.295}
                color="#949BBF"
                anchorX="center"
                fillOpacity={2.0}
                position={[0.035, 0.02, 0]}
            >
                {value}
            </Text>
        </group>
    );
};
